<script setup>
// Ring chart + legend (docs/analytics/SPEC.md, decisions 2, 10). Legend rows expand in place to the
// securities behind them (grouped by industry when the share has several); hovering a row or an arc
// highlights both.
import { computed, ref } from 'vue';
import { intlLocale, t } from '../i18n';
import { isPhone } from '../isPhone';
import AllocationItemRow from './AllocationItemRow.vue';
import ShareBar from './ShareBar.vue';

const props = defineProps({
  // Legend rows: [{ key, label, value, percent, color, items?, groups?, position? }].
  // A row with `position` is a security itself (companies list).
  slices: {
    type: Array,
    required: true,
  },
  // Arcs, when they differ from the rows: [{ key, label, value, percent, color, members? }]
  ring: {
    type: Array,
    default: null,
  },
  total: {
    type: Number,
    default: 0,
  },
  // Whole portfolio value: industry subheaders show their share of it.
  portfolioTotal: {
    type: Number,
    default: 0,
  },
  centerLabel: {
    type: String,
    default: '',
  },
  // Rows shown below lg before "Show all" (docs/design/SPEC.md, decision 4); null = all.
  limit: {
    type: Number,
    default: null,
  },
  numbered: {
    type: Boolean,
    default: false,
  },
  // Rows folded into one gray row and arc until "Show more" (docs/analytics/SPEC.md, decision 16):
  // { key, label, value, percent, color, members }
  others: {
    type: Object,
    default: null,
  },
});

const expanded = defineModel('expanded', { type: Array, default: () => [] });

const active = ref(null);
const showAll = ref(false);
const showOthers = ref(false);

function hiddenOnSmall(index) {
  return props.limit !== null && !showAll.value && index >= props.limit;
}
const rowRefs = new Map();

const folded = computed(() => props.others !== null && !showOthers.value);

const rows = computed(() => {
  if (!folded.value) {
    return props.slices;
  }
  const members = new Set(props.others.members);
  return props.slices.filter((s) => !members.has(s.key));
});

const arcsSource = computed(() => props.ring ?? (folded.value ? [...rows.value, props.others] : props.slices));

// Long totals ("12 345 678 ₽") take a smaller size so they stay clear of the ring.
const totalText = computed(() => money(props.total));
const totalLong = computed(() => totalText.value.length > 11);

// Gap between arcs, in % of the ring.
const GAP = 0.6;

// Arc lengths by value: rings whose shares are of a bigger total (companies without cash) stay closed.
const arcs = computed(() => {
  const sum = arcsSource.value.reduce((s, a) => s + a.value, 0);
  const gap = arcsSource.value.length > 1 ? GAP : 0;
  let start = 0;
  return arcsSource.value.map((slice) => {
    const share = sum ? (slice.value / sum) * 100 : 0;
    const length = Math.max(share - gap, 0.15);
    const arc = { ...slice, dash: `${length} ${100 - length}`, offset: -start };
    start += share;
    return arc;
  });
});

const activeArc = computed(() => arcsSource.value.find((a) => a.key === active.value
  || a.members?.includes(active.value)) ?? null);

function arcHighlighted(arc) {
  return active.value !== null && (arc.key === active.value || arc.members?.includes(active.value));
}

function rowHighlighted(key) {
  return active.value !== null && (key === active.value || activeArc.value?.members?.includes(key) === true);
}

function canExpand(slice) {
  return !slice.position && (slice.items?.length ?? 0) > 0;
}

function isExpanded(key) {
  return expanded.value.includes(key);
}

function toggle(key) {
  expanded.value = isExpanded(key) ? expanded.value.filter((k) => k !== key) : [...expanded.value, key];
}

// A tap on an arc opens its row and brings it into view (phones have no hover); the gray arc unfolds.
function onArcClick(arc) {
  if (props.others && arc.key === props.others.key) {
    unfoldOthers();
    return;
  }
  const slice = props.slices.find((s) => s.key === arc.key);
  if (slice && canExpand(slice)) {
    if (!isExpanded(slice.key)) {
      toggle(slice.key);
    }
    rowRefs.get(slice.key)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

function unfoldOthers() {
  showOthers.value = true;
  active.value = null;
}

function setRowRef(key, el) {
  if (el) {
    rowRefs.set(key, el);
  } else {
    rowRefs.delete(key);
  }
}

function money(value) {
  return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
}

function percent(value) {
  return `${new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value)} %`;
}

function shareOfPortfolio(value) {
  return props.portfolioTotal ? (value / props.portfolioTotal) * 100 : 0;
}
</script>

<template>
  <div class="grid gap-5 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-start lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-8">
    <div class="relative mx-auto size-40 sm:sticky sm:top-20 sm:mx-0 sm:size-44 lg:size-[260px]">
      <svg
        viewBox="0 0 100 100"
        class="size-full -rotate-90"
        role="img"
        :aria-label="centerLabel"
      >
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="currentColor"
          class="text-slate-800"
          stroke-width="12"
        />
        <circle
          v-for="arc in arcs"
          :key="arc.key"
          cx="50"
          cy="50"
          r="40"
          fill="none"
          pathLength="100"
          :stroke="arc.color"
          :stroke-width="arcHighlighted(arc) ? 15 : 12"
          :stroke-dasharray="arc.dash"
          :stroke-dashoffset="arc.offset"
          :opacity="active === null || arcHighlighted(arc) ? 1 : 0.35"
          class="cursor-pointer transition-all duration-150"
          @mouseenter="active = arc.key"
          @mouseleave="active = null"
          @click="onArcClick(arc)"
        />
      </svg>
      <!-- Inside the hole with a margin: the hole is 65 % of the ring even with a highlighted arc
           (r 40 − stroke 15 / 2), the text box is 58 %, the label narrower still for its corners -->
      <div class="pointer-events-none absolute inset-[21%] flex flex-col items-center justify-center text-center">
        <template v-if="activeArc">
          <span class="line-clamp-2 max-w-[80%] text-[0.625rem] leading-tight text-slate-400 lg:text-[0.6875rem]">{{ activeArc.label }}</span>
          <span class="mt-0.5 whitespace-nowrap text-[0.9375rem] font-semibold text-slate-100 lg:text-lg">{{ percent(activeArc.percent) }}</span>
          <span class="whitespace-nowrap text-[0.625rem] text-slate-400 lg:text-[0.6875rem]">{{ money(activeArc.value) }}</span>
        </template>
        <template v-else>
          <span class="line-clamp-2 max-w-[80%] text-[0.625rem] leading-tight text-slate-400 lg:text-[0.6875rem]">{{ centerLabel }}</span>
          <span
            class="mt-0.5 whitespace-nowrap font-semibold text-slate-100"
            :class="totalLong ? 'text-[0.8125rem] lg:text-base' : 'text-[0.9375rem] lg:text-lg'"
          >{{ totalText }}</span>
        </template>
      </div>
    </div>

    <ul
      class="w-full min-w-0 text-[0.8125rem] lg:text-xs"
      :class="isPhone ? '' : 'space-y-0.5'"
    >
      <li
        v-for="(slice, index) in rows"
        :key="slice.key"
        :ref="(el) => setRowRef(slice.key, el)"
        :class="hiddenOnSmall(index) ? 'hidden lg:block' : ''"
      >
        <AllocationItemRow
          v-if="slice.position"
          :item="{ position: slice.position, value: slice.value, percent: slice.percent }"
          :color="slice.color"
          :number="numbered ? index + 1 : null"
          :indent="false"
          :bars="!isPhone"
          :active="rowHighlighted(slice.key)"
          @mouseenter="active = slice.key"
          @mouseleave="active = null"
        />
        <template v-else>
          <!-- Phone: label with the sum under it, the percent on the right (docs/design/SPEC.md, decision 14) -->
          <button
            v-if="isPhone"
            type="button"
            class="-mx-2 flex min-h-12 w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-2 text-left active:bg-slate-800"
            :class="canExpand(slice) ? '' : 'cursor-default'"
            :aria-expanded="canExpand(slice) ? isExpanded(slice.key) : undefined"
            @click="canExpand(slice) && toggle(slice.key)"
          >
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: slice.color }"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1">
              <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ slice.label }}<span
                v-if="canExpand(slice)"
                class="text-[0.8125rem] text-slate-500"
              > · {{ slice.items.length }}</span></span>
              <span class="mt-0.5 block text-[0.8125rem] leading-4 tabular-nums text-slate-500">{{ money(slice.value) }}</span>
            </span>
            <span class="shrink-0 text-[0.9375rem] tabular-nums text-slate-100">{{ percent(slice.percent) }}</span>
            <svg
              class="size-4 shrink-0 text-slate-500 transition-transform"
              :class="[isExpanded(slice.key) ? 'rotate-90' : '', canExpand(slice) ? '' : 'invisible']"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
          <button
            v-else
            type="button"
            class="flex w-full items-center gap-3 rounded px-2 py-1.5 text-left"
            :class="[rowHighlighted(slice.key) ? 'bg-slate-800/80' : '', canExpand(slice) ? 'hover:bg-slate-800/80' : 'cursor-default']"
            :aria-expanded="canExpand(slice) ? isExpanded(slice.key) : undefined"
            :title="slice.label"
            @mouseenter="active = slice.key"
            @mouseleave="active = null"
            @click="canExpand(slice) && toggle(slice.key)"
          >
            <span class="flex min-w-0 flex-1 items-center gap-2 sm:w-60 sm:flex-none lg:w-72">
              <span
                class="size-2.5 shrink-0 rounded-full"
                :style="{ backgroundColor: slice.color }"
                aria-hidden="true"
              />
              <span class="min-w-0 truncate text-slate-200">
                {{ slice.label }}
                <span
                  v-if="canExpand(slice)"
                  class="ml-1 text-[0.6875rem] text-slate-500 lg:text-[0.625rem]"
                  :title="t('analytics.itemsCount', { count: slice.items.length })"
                >· {{ slice.items.length }}</span>
              </span>
            </span>
            <ShareBar
              :percent="shareOfPortfolio(slice.value)"
              :color="slice.color"
            />
            <span class="flex w-20 shrink-0 flex-col items-end sm:w-auto sm:flex-row sm:items-center">
              <span class="tabular-nums text-slate-100 sm:w-14 sm:text-right">{{ percent(slice.percent) }}</span>
              <span class="text-[0.6875rem] tabular-nums text-slate-500 sm:w-28 sm:text-right sm:text-xs lg:text-[0.6875rem]">{{ money(slice.value) }}</span>
            </span>
            <svg
              class="size-3.5 shrink-0 text-slate-500 transition-transform"
              :class="[isExpanded(slice.key) ? 'rotate-90' : '', canExpand(slice) ? '' : 'invisible']"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <div
            v-if="isExpanded(slice.key)"
            class="mb-1"
          >
            <template v-if="(slice.groups?.length ?? 0) > 1">
              <template
                v-for="group in slice.groups"
                :key="group.key"
              >
                <p
                  v-if="isPhone"
                  class="mt-2 flex items-baseline justify-between gap-3 pl-5 text-[0.75rem] uppercase tracking-wide text-slate-500"
                >
                  <span class="min-w-0 truncate">{{ group.label }}</span>
                  <span class="shrink-0 tabular-nums normal-case">{{ percent(shareOfPortfolio(group.value)) }}</span>
                </p>
                <div
                  v-else
                  class="mt-1 flex items-center gap-3 rounded px-2 py-1 text-[0.6875rem] text-slate-400 lg:text-[0.625rem]"
                  :class="rowHighlighted(group.key) ? 'bg-slate-800/80' : ''"
                  @mouseenter="active = group.key"
                  @mouseleave="active = null"
                >
                  <span class="min-w-0 flex-1 truncate pl-5 sm:w-60 sm:flex-none lg:w-72">{{ group.label }}</span>
                  <span
                    class="hidden flex-1 sm:block"
                    aria-hidden="true"
                  />
                  <span class="flex w-20 shrink-0 flex-col items-end sm:w-auto sm:flex-row sm:items-center">
                    <span class="tabular-nums sm:w-14 sm:text-right">{{ percent(shareOfPortfolio(group.value)) }}</span>
                    <span class="tabular-nums sm:w-28 sm:text-right">{{ money(group.value) }}</span>
                  </span>
                  <span
                    class="w-3.5 shrink-0"
                    aria-hidden="true"
                  />
                </div>
                <AllocationItemRow
                  v-for="item in group.items"
                  :key="item.key"
                  :item="item"
                  :bar-color="slice.color"
                />
              </template>
            </template>
            <template v-else>
              <AllocationItemRow
                v-for="item in slice.items"
                :key="item.key"
                :item="item"
                :bar-color="slice.color"
              />
            </template>
          </div>
        </template>
      </li>

      <!-- Everything past the top 10 as one row; a tap unfolds it like "Show more" -->
      <li v-if="folded">
        <button
          v-if="isPhone"
          type="button"
          class="-mx-2 flex min-h-12 w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-2 text-left active:bg-slate-800"
          @click="unfoldOthers"
        >
          <span
            v-if="numbered"
            class="w-5 shrink-0"
            aria-hidden="true"
          />
          <span
            class="size-2.5 shrink-0 rounded-full"
            :style="{ backgroundColor: others.color }"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ others.label }}</span>
            <span class="mt-0.5 block text-[0.8125rem] leading-4 tabular-nums text-slate-500">{{ money(others.value) }}</span>
          </span>
          <span class="shrink-0 text-[0.9375rem] tabular-nums text-slate-100">{{ percent(others.percent) }}</span>
        </button>
        <button
          v-else
          type="button"
          class="flex w-full items-center gap-3 rounded px-2 py-1 text-left hover:bg-slate-800/80"
          :class="rowHighlighted(others.key) ? 'bg-slate-800/80' : ''"
          @mouseenter="active = others.key"
          @mouseleave="active = null"
          @click="unfoldOthers"
        >
          <span class="flex min-w-0 flex-1 items-center gap-2 sm:w-60 sm:flex-none lg:w-72">
            <span
              v-if="numbered"
              class="w-5 shrink-0"
              aria-hidden="true"
            />
            <span
              class="size-2 shrink-0 rounded-full"
              :style="{ backgroundColor: others.color }"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate text-slate-200">{{ others.label }}</span>
          </span>
          <ShareBar
            :percent="others.percent"
            :color="others.color"
            thin
          />
          <span class="flex w-20 shrink-0 flex-col items-end sm:w-auto sm:flex-row sm:items-center">
            <span class="tabular-nums text-slate-100 sm:w-14 sm:text-right">{{ percent(others.percent) }}</span>
            <span class="text-[0.6875rem] tabular-nums text-slate-500 sm:w-28 sm:text-right sm:text-xs lg:text-[0.6875rem]">{{ money(others.value) }}</span>
          </span>
          <span
            class="w-3.5 shrink-0"
            aria-hidden="true"
          />
        </button>
      </li>
      <!-- In the list column, right under the rows (not under the taller ring) -->
      <li
        v-if="others"
        :class="isPhone ? 'pt-3' : 'pt-1'"
      >
        <button
          type="button"
          :class="isPhone
            ? 'h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-700'
            : 'rounded px-2 py-1.5 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
          @click="folded ? unfoldOthers() : (showOthers = false)"
        >
          {{ folded ? t('analytics.showMore', { count: others.members.length }) : t('analytics.showLess') }}
        </button>
      </li>
    </ul>
    <button
      v-if="limit !== null && !showAll && slices.length > limit"
      type="button"
      :class="isPhone
        ? 'h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-700'
        : 'rounded px-2 py-1.5 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200 sm:col-start-2 lg:hidden'"
      @click="showAll = true"
    >
      {{ t('analytics.showAll', { count: slices.length }) }}
    </button>
  </div>
</template>
